<template>
  <loadingCube v-if="loading" />

  <div v-else class="pane">
    <PageHeading :back="back">
      <template #title>
        <span>
          <router-link :to="{ name: 'company_record', query: { company_id } }">
            {{ company?.company_name }}
          </router-link>
        </span>
        <br />
        <router-link :to="back">Submit Sites</router-link>
        ->
        Import timesheets
      </template>
    </PageHeading>

    <ValidationObserver ref="uploadTimesheetFileForm" slim>
      <form role="form" @submit.prevent="upload_file" novalidate>
        <div class="pane" style="display:grid;grid-template-columns:100%;">
          <div class="pane">
            <h3>Import timesheets</h3>
            <file_input
                @file-chosen="file_select"
                :required="true"
                :selected_file="file"
                :hidden_id="'timesheet_file'"
                :file_error_name="'Timesheet File'"
                :accepted_file_type="'.xlsx,.xls,.csv'"
            />
            <p v-if="fileErrorText" class="text-danger" style="margin-top:0.5rem;">
              {{ fileErrorText }}
            </p>
          </div>
        </div>

        <div class="row">
          <div class="col-12"><hr /></div>
          <div class="col-12">
            <div class="float-right">
              <button :disabled="saving || !file" class="btn orcaBtn sm_btn" type="submit">
                <span v-if="saving" class="btnLoading"><i class="fas fa-spinner fa-pulse"></i>Uploading ...</span>
                <span v-else>UPLOAD TIMESHEET FILE</span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import file_input from 'partials/files/fileInput.vue';
import loadingCube from 'loaders/record_loader.vue';
import PageHeading from 'pages/sunset/timesheets/components/shared/PageHeading.vue';

export default {
  name: 'admin_select_timesheet_file_to_upload',
  components: { PageHeading, loadingCube, file_input },
  props: {
    company_id: { required: true },
    start_date: { required: true },
    end_date: { required: true },
  },
  data() {
    return {
      back: { name: 'a_timesheet_r2_select_sites', params: { company_id: this.company_id, start_date: this.start_date, end_date: this.end_date } },
      loading: true,
      file: null,
      company: null,
      saving: false,
      sites: [],
      templateDownloading: false,
      fileErrorText: '',
      errorSig: '',
      importId: this.$route.params.importId ? Number(this.$route.params.importId) : null,
    };
  },
  async beforeMount() {
    this.loading = true;

    await this.fetchSites();
    if (this.allSitesCompleted) {
      await this.$router.push(this.back);
      return;
    }

    await this.fetchSupportingData();

    if (this.importId) {
      await this.$router.replace({
        name: 'a_timesheet_import_processing',
        params: {
          company_id: this.company_id,
          importId: this.importId,
          start_date: this.start_date,
          end_date: this.end_date,
        },
      });
      return;
    }

    this.loading = false;
  },
  computed: {
    allSitesCompleted() { return this.sites.every(i => i.completed); },
  },
  methods: {
    importBasePath() {
      return `${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/imports`;
    },

    async fetchSites() {
      const res = await axios.get(`${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/sites?submission_date=${this.end_date}`);
      if (res.status === 200) this.sites = res.data.data;
    },

    async fetchSupportingData() {
      const res = await axios.get(this.crmApiPath + 'company/' + this.company_id);
      if (res.status === 200) this.company = res.data;
    },

    // idempotent inline error setter
    setFileError(msgOrArray) {
      const arr = Array.isArray(msgOrArray) ? msgOrArray : [msgOrArray];
      const uniq = Array.from(new Set(arr.map(s => String(s || '').trim()).filter(Boolean)));
      const msg = uniq.join(' ');
      if (msg && msg === this.errorSig) return;
      this.errorSig = msg;
      this.fileErrorText = msg;
    },

    clearFileError() {
      this.fileErrorText = '';
      this.errorSig = '';
    },

    file_select(file) {
      this.file = file;
      this.clearFileError();
    },

    async downloadTimesheetTplFile() {
      // downloads the current template for this submission window
      this.templateDownloading = true;
      try {
        const response = await axios.post(this.crmApiPath + 'timesheet/download_timesheet_tpl', {
          company_id: this.company_id,
          submission_date: this.end_date,
        }, { responseType: 'blob' });

        const filename = response.headers['custom-filename'] || 'Timesheet_Template.xlsx';
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url; link.setAttribute('download', filename);
        document.body.appendChild(link); link.click(); link.remove();
      } catch (err) {
        this.$snotify.error('Template download failed');
      } finally {
        this.templateDownloading = false;
      }
    },

    async upload_file() {
      this.clearFileError();

      const ok = await this.$refs.uploadTimesheetFileForm.validate();
      if (!ok) {
        this.formErrorsValidationScroll(this.$refs.uploadTimesheetFileForm);
        return;
      }

      if (!this.file) {
        this.setFileError('Please choose a file.');
        return;
      }

      const name = this.file?.name || '';
      if (!/\.(csv|xls|xlsx)$/i.test(name)) {
        this.setFileError('The timesheet file must have a .csv, .xls or .xlsx extension.');
        return;
      }

      this.saving = true;
      try {
        const formData = new FormData();
        formData.append('company_id', this.company_id);
        formData.append('submission_date', this.end_date);
        formData.append('timesheet_file', this.file);

        const idem = (window.crypto && crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;

        const { data } = await axios.post(this.importBasePath(), formData, {
          headers: { 'Content-Type': 'multipart/form-data', 'Idempotency-Key': idem },
        });

        if (data && data.id) {
          this.importId = Number(data.id);
          await this.$router.replace({
            name: 'a_timesheet_import_processing',
            params: {
              company_id: this.company_id,
              importId: this.importId,
              start_date: this.start_date,
              end_date: this.end_date,
            },
          });
        } else {
          this.$snotify.error('Could not queue import');
        }
      } catch (e) {
        const res = e?.response;
        const errs = res?.data?.validation_errors || res?.data?.errors || {};
        const fileErrs = errs?.timesheet_file;

        if (fileErrs) {
          this.setFileError(fileErrs);
          this.formErrorsValidationScroll?.(this.$refs.uploadTimesheetFileForm);
        } else {
          const msg = res?.data?.message || 'Upload failed - please try again.';
          this.$snotify.error(msg);
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss">
.page_heading { padding-left: 1em; margin-bottom: 2em; }
.pane { padding: 1em; background-color: white; border: 1px solid #eee; border-radius: 10px; margin-bottom: 1em; }
.text-danger { color: #d9534f; }
</style>
